import { any, clone, descend, toUpper, filter, find, includes, isEmpty, isNil, is, join, keys, length as rlength, prop, propEq, sort, split, startsWith } from 'ramda';
import moment from 'moment';
import { AssetsURL } from './BlobStorageApi';
import pkg from '../../../../package.json';

/**
 * @author Hiroki Moto
 * @description Get version number of Package.json
 * We update package.json version for QA/Production release.
 * Once we release the new version, the app should initialize all the stores.
 * That's why we get this version number
 */
export const version = () => {
    return pkg.version;
};

/**
 * @author Hiroki Moto
 * @description Get page numbers for pagination.
 * Example Usage: pagination(1, 10)
 * Example Response: 1, 2, 3, ..., 9, 10
 * @param c current page number of the pagination
 * @param m total page counts of the pagination
 * @returns page range numbers with "..."
 */
export const pagination = (c, m) => {
    var currentPage = c,
        last = m,
        delta = 3,
        left = currentPage - delta,
        right = currentPage + delta + 1,
        range = [],
        rangeWithDots = [],
        l;

    for (let i = 1; i <= last; i++) {
        if (i == 1 || i == last || (i >= left && i < right)) {
            range.push(i);
        }
    }

    for (let i of range) {
        if (l) {
            if (i - l === 2) {
                rangeWithDots.push(l + 1);
            } else if (i - l !== 1) {
                rangeWithDots.push('...');
            }
        }
        rangeWithDots.push(i);
        l = i;
    }

    return rangeWithDots;
};

/**
 * @author Hiroki Moto
 * @description Verify if its a valid url
 * @param url: URL to verify if its a valid url or not.
 * @return true/false
 */
export const validateUrl = (url) => {
    const regExp = /(https?:\/\/.*(\.(?:png|jpg|mp4|PNG|JPG|MP4))?)/g;
    return regExp.test(url);
};

/**
 * @description Verify if string is base64 or not
 * @param url: string
 * @return true/false
 */
export const checkBase64Format = (base64DataString) => {
    let base64regex = /^data:image\/(?:gif|png|jpeg|bmp|webp)(?:;charset=utf-8)?;base64,(?:[A-Za-z0-9]|[+/])+={0,2}/g;
    return base64regex.test(base64DataString);
};

/**
 * @description Verify if its a formatted image url or not
 * @param url: URL to verify if its a valid url or not.
 * @return formattedUrl
 */
export const formattedUrl = (url) => {
    let findNullOrUrl = includes('[]', url) || includes('[""]', url) ? '' : url;
    let validUrl = startsWith('//', findNullOrUrl) ? 'https:' + findNullOrUrl : findNullOrUrl;
    if (includes('///', validUrl)) {
        const splitUrl = split('///', validUrl);
        return 'https://' + splitUrl[1];
    }
    return validUrl;
};

/**
 * @param url : abbreviated image url
 * @param defaultImg : default image when url is empty
 * @return : full image url
 */
export const convertUrl = (url, defaultImg = undefined) => {
    const regex = /(data:image\/[^;]+;base64[^"]+)/;
    if (!url) {
        return defaultImg;
    } else if (regex.test(url)) {
        return url;
    } else if (includes('/images/', url) && /\.(svg|jpg|png)$/i.test(url)) {
        return url;
    }

    return validateUrl(url) ? url : formattedUrl(`${AssetsURL}${url}`);
};

/**
 * @param str : string to validate
 * @param errorMessage : return error message
 * @param func : function that validate
 * @param options : arguments for func
 * @return : error message
 */

export const hasError = (str = '', func = () => {}, errorMessage = '') => {
    return func(str) ? errorMessage : '';
};

export const getImageURL = (str) => {
    if (typeof window === 'undefined') return '';
    const div = document.createElement('div');
    div.innerHTML = str;
    const firstImage = div.getElementsByTagName('img')[0];
    const imgSrc = firstImage ? firstImage.src : '';
    return imgSrc;
};

export const blobToBase64 = (blob) =>
    new Promise((resolve, reject) => {
        if (!blob) {
            resolve(null);
        } else {
            const reader = new FileReader();
            reader.readAsDataURL(blob);
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = (error) => resolve(null);
        }
    });

export const getProductVideoLink = (productId, video) => {
    const link = `https://ds-uploads-${process.env.NODE_ENV}.s3-us-west-2.amazonaws.com/content/${productId}-${video}/Video.mp4`;
    return link;
};

export const removeTags = (text) => {
    const regex = /(<([^>]+)>)/gi;
    if (text) return text.replace(regex, '');
    return text;
};

export const length = (e) => {
    if (isNil(e) || isEmpty(e)) return 0;
    return rlength(e);
};

export const percent = (some, total) => {
    if (some === 'N/A') return 'N/A';
    else if (total === 0) return '0' + '%';
    return Math.round(((100 * some) / total).toFixed(2)) + '%';
};

export const convertAvgDays = (time) => {
    if (!time) return 0;

    const days = parseInt(split('days', time)[0]);
    const mSeconds = moment.duration(split('days', time)[1]).asSeconds();
    const avgTime = (days + moment.duration(mSeconds).asDays()).toFixed(1);

    if (avgTime < 10) return avgTime + ' days';
    else if (avgTime <= 30) return Math.round(avgTime) + ' days';
    else return '99 days';
};

export const toDuration = (e) => {
    const start = moment(e);
    const end = moment();
    const hours = moment.duration(end.diff(start)).asHours();
    const days = moment.duration(end.diff(start)).asDays();
    const weeks = moment.duration(end.diff(start)).asWeeks();
    const months = moment.duration(end.diff(start)).asMonths();

    if (hours < 24) {
        const _hours = Math.round(hours);
        return `${_hours} ${_hours === 1 ? 'hr' : 'hrs'}`;
    } else if (days < 30) {
        const _days = Math.round(days);
        return `${_days} ${_days === 1 ? 'day' : 'days'}`;
    } else if (months > 1) {
        const _months = Math.round(months);
        return `${_months} ${_months === 1 ? 'month' : 'months'}`;
    }
};

export const youtubeURL = (url) => {
    if (!url) return false;
    var regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#\&\?]*).*/;
    var match = url.match(regExp);
    return match && match[7].length == 11 ? match[7] : false;
};

export const isImageURL = (url) => {
    if (url && url !== '' && typeof url === 'string') {
        return url.match(/\.(jpeg|jpg|gif|png|JPEG|JPG|GIF|PNG)$/) != null;
    }
    return false;
};

export const getSettings = (ids, data, empty) => {
    if (typeof ids === 'object' && !Array.isArray(ids)) return empty;
    if (isNil(ids) || isNil(data)) return empty;
    let items = [];
    ids.map((id) => {
        const item = find(propEq('id', id), data);
        if (item) items.push(item);
    });
    items = items.map((e) => e.name);
    let placeholder = join(', ', items);
    if (isEmpty(placeholder)) placeholder = empty;
    return placeholder;
};

/**
 *
 * @param {string} name
 * @description get initials from string
 */
export const getInitials = (name) => {
    if (!name) return '';
    const matches = name.match(/\b(\w)/g);
    const initials = matches.join('');
    return initials;
};

/**
 * Check if that the index might be belong to the page range.
 * @param {number} index
 * @param {number} page
 * @param {number} per
 */
export const inPage = (index, page, per) => {
    return index >= (page - 1) * per && index < page * per;
};

/**
 * We use this export const to initialize data
 * For instance, in Manage/Report/Career, we initialize the program using this export const.
 * @param {any} data
 */
export const initialize = (e) => {
    // Manager Report Career
    let program = clone(e);
    let levels = keys(program?.data?.levels) || [];
    if (!program.quotas) program.quotas = [];
    if (!program.habits) program.habits = [];
    if (!program.trainings) program.trainings = [];

    levels.forEach((level) => {
        program.data.levels[level].quotas = program.library?.levels[level]?.quotas;
        program.data.levels[level].habits = clone(program.library?.levels[level]?.habits);
        program.data.levels[level].trainings.items = program.library?.levels[level]?.trainings;
    });

    levels.forEach((level) => {
        const _quotas = filter(propEq('program_level', Number(level)), program.quotas);
        const _habits = filter(propEq('program_level', Number(level)), program.habits);
        const _trainings = filter(propEq('program_level', Number(level)), program.trainings);

        const dayHabits = filter((e) => e?.data?.schedule_interval === 'day', _habits);
        const weekHabits = filter((e) => e?.data?.schedule_interval === 'week', _habits);
        const monthHabits = filter((e) => e?.data?.schedule_interval === 'month', _habits);

        if (rlength(_quotas) > 0) {
            program.data.levels[level].quotas = _quotas;
        }
        if (rlength(_habits) > 0) {
            program.data.levels[level].habits = { day: dayHabits, week: weekHabits, month: monthHabits };
        }
        if (rlength(_trainings) > 0) {
            program.data.levels[level].trainings.items = _trainings;
        }
    });

    return program;
};

export const getUnit = (e, unit) => {
    let value = Math.floor((e || 0) * 100) / 100;
    value = e !== null ? Number(value).toLocaleString('en') : 'Na';
    if (isNil(unit)) {
        return value;
    } else if (unit === 'Percentage') {
        return value + ' %';
    } else if (unit === 'Integer' || unit === 'Number' || unit === 'Quantity' || unit === 'Task' || unit === 'Text') {
        return value;
    } else if (unit === 'Dollar') {
        return '$ ' + value;
    }
};

export const isValid = (e) => {
    return !isEmpty(e) && !isNil(e);
};

export const removeAssign = (data, published) => {
    if (published === 0) {
        const options = filter((e) => !includes('Assign', e), data);
        return options;
    } else {
        return data;
    }
};

export const localDate = (date, formatString = 'YYYY-MM-DD') => {
    const isString = is(String);
    return isString(date) ? moment.utc(date).local().format(formatString) : moment.unix(date).local().format(formatString);
};

/**
 * @description NO OPERATION!
 * Mostly it will be used as defaultProps for export const props.
 */
export const noop = () => {};

/**
 * @param {object} quota quota object
 * @param {number} actual actual value
 */
export const quotaCalc = (quota, actual) => {
    const { star_values, quota_direction } = quota.data;
    const actValue = Number(actual);

    if (star_values) {
        let star = 0;
        for (let i = 0; i < star_values.length; i++) {
            const val = star_values[i];
            if (actValue === val) {
                star = i;
                break;
            }
            if (quota_direction === 2) {
                if (actValue >= val && i > 0) {
                    const prev = star_values[i - 1];
                    const diff = prev - actValue > 0 ? prev - actValue : 0;
                    const span = prev - val;

                    if (span > 0) {
                        const perc = diff / span;
                        star = star + perc;
                    }
                    break;
                }
            } else {
                if (actValue <= val && i > 0) {
                    const prev = star_values[i - 1];
                    const diff = actValue - prev > 0 ? actValue - prev : 0;
                    const span = val - prev;
                    if (span > 0) {
                        const perc = diff / span;
                        star = star + perc;
                    }
                    break;
                }
            }
            star = i;
        }
        return Number(star).toFixed(2);
    }
    return 'N/A';
};

export const removeStatus = (status, from) => {
    return filter((e) => e !== status, from);
};

export const loading = (status, query = 'pending') => {
    return any((e) => includes(query, e))(status);
};

export const arrangeQuota = (stars, order) => {
    if (!stars || stars.length === 0) return [];
    const diff = (order) =>
        function (a, b) {
            if (order === 1) return a - b;
            else return b - a;
        };
    const data1 = sort(diff(order), stars);
    const data2 = data1.map((item, index) => ({ value: item, star: index }));
    return sort(descend(prop('star')), data2);
};

export const formatPhoneNumber = (pNum) => {
    const x = pNum.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
    return !x[2] ? x[1] : `(${x[1]}) ${x[2]}${x[3] ? '-' + x[3] : ''}`;
};

export const getValidPhoneNumber = (num) => {
    return num.replace(/\D/g, '');
};

export const urlify = (text) => {
    const urlRegex = /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi;
    return text.replace(urlRegex, function (url) {
        return '<a target="_blank" href="' + url + '">' + url + '</a>';
    });
};

export const processCsvData = (dataString) => {
    const dataStringLines = dataString.split(/\r\n|\n/);
    const headers = dataStringLines[0].split(/,(?![^"]*"(?:(?:[^"]*"){2})*[^"]*$)/);

    const list = [];
    for (let i = 1; i < dataStringLines.length; i++) {
        const row = dataStringLines[i].split(/,(?![^"]*"(?:(?:[^"]*"){2})*[^"]*$)/);
        if (headers && row.length == headers.length) {
            const obj = {};
            for (let j = 0; j < headers.length; j++) {
                let d = row[j];
                if (d.length > 0) {
                    if (d[0] == '"') d = d.substring(1, d.length - 1);
                    if (d[d.length - 1] == '"') d = d.substring(d.length - 2, 1);
                }
                if (headers[j]) {
                    obj[headers[j]] = d;
                }
            }
            if (Object.values(obj).filter((x) => x).length > 0) {
                list.push(obj);
            }
        }
    }

    return list;
};

export const getHtmlIframes = (html = '') => {
    return html.match(/(?:<iframe[^>]*)(?:(?:\/>)|(?:>.*?<\/iframe>))/g) || [];
};

export const htmlHasImage = (html = '') => {
    return /<img[^>]* src=\"([^\"]*)\"[^>]*>/g.test(html);
};

export const hasHtml = (str = '') => /<[a-z][\s\S]*>/i.test(str);

export const isQuillEmpty = (contents) => {
    return JSON.stringify(contents) === '{"ops":[{"insert":"\\n"}]}';
};

export const createImageElement = (file) => {
    return new Promise((resolve, reject) => {
        const img = document.createElement('img');
        const reader = new FileReader();

        reader.onloadend = () => {
            img.src = reader.result;
            resolve(img);
        };

        file ? reader.readAsDataURL(file) : resolve(null);
    });
};

export const elementHasImage = (selector) => {
    return !!document.querySelector(`${selector} img`);
};

export const isNotNull = (val, key) => !isNil(val);

export const getCompanyName = (business) => business?.name || business?.vrs?.name || business?.hcms?.name;

export const getHtmlImages = (html) => {
    return html.match(/<img[^>]* src=\"([^\"]*)\"[^>]*>/g) || [];
};

export const capitalize = (str) => {
    return str.replace(/^./, toUpper);
};

export const validateEmail = (email) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
};

export const stringToHex = (str) => {
    if (!str) return 'unset';
    let hash = 0;
    if (str.length === 0) return hash;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
        hash = hash & hash;
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
        let value = (hash >> (i * 8)) & 255;
        color += ('00' + value.toString(16)).substr(-2);
    }
    return color;
};

export const convertToSlug = (e) => {
    return e
        .toLowerCase()
        .replace(/ /g, '-')
        .replace(/[^\w-]+/g, '');
};

export const formatDate = (e, type = 'MMM DD, YY', placeholder = 'Na') => {
    return e ? moment(e).format(type) : placeholder;
};

export const scorecards = (item, dateStart, dateEnd, type = 'status') => {
    let scorecard;
    if (item?.scorecards?.length > 0) scorecard = item.scorecards[0];
    if (item?.scorecard) scorecard = item.scorecard;
    const date = `${formatDate(dateStart, 'YYYY-MM')}-15`;
    const status = item.performance?.status;

    if (performance) {
        const quotas = scorecard?.data?.quotas ?? [];
        if (!quotas.length) return type == 'status' ? 'No Scorecard' : 'Na';

        if (type == 'status') {
            switch (status) {
                case 2: {
                    return 'Completed';
                    break;
                }
                case 1: {
                    return 'Started';
                    break;
                }
                case 0: {
                    return 'Created';
                    break;
                }
                default: {
                    return 'Incomplete';
                    break;
                }
            }
        } else {
            return status == 2 ? '100%' : `${scorecard.data[date]?.percent || 0}%`;
        }
    } else {
        return type == 'status' ? 'No Scorecard' : 'Na';
    }
};

export const messages = (type) => {
    switch (type) {
        case 'habitslist':
            return 'Your habit schedule template was successfully saved.';
        case 'quotas':
            return 'Your quota template was successfully saved.';
        case 'scorecards':
            return 'Your scorecard template was successfully saved.';
        case 'tracks':
            return 'Your track was successfully saved.';
        case 'courses':
            return 'Your course was successfully saved.';
        case 'modules':
            return 'Your modules was successfully saved.';
        default:
            return 'Your habit template was successfully saved.';
    }
};

export const isFileObj = (file) => typeof file.name === 'string';

export const getAvatarUrl = (thumbnail) => {
    if (thumbnail && typeof thumbnail !== 'object' && thumbnail.includes('undefined')) {
        return null;
    }
    return thumbnail;
};
